import { useState } from "react";
import { Loader2, PackageSearch, X as XIcon, Truck } from "lucide-react";
import { toast } from "sonner";
import { fetchLastOrders } from "@/lib/api";

const STATUSES = [
  { key: "", label: "Any status" },
  { key: "processing", label: "Processing" },
  { key: "completed", label: "Complete" },
];

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

// CRM composer: pull the most recent order for every selected contact so the
// Order Confirmation / Order Shipped templates merge real items, totals + tracking.
export default function LastOrderLoader({ recId, contacts = [], loaded, onLoaded, onClear }) {
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  if (!contacts.length) return null;

  async function load() {
    setLoading(true);
    try {
      const res = await fetchLastOrders(recId, contacts.map((c) => c.id), status ? [status] : []);
      const orders = res.orders || [];
      if (!orders.length) {
        toast.error(status ? `No ${status} orders found for the selected contacts` : "No orders found for the selected contacts");
        return;
      }
      onLoaded(orders);
      const skipped = (res.missing || []).length;
      toast.success(`Loaded last order for ${orders.length} contact${orders.length === 1 ? "" : "s"}` + (skipped ? ` · ${skipped} without an order skipped` : ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load orders");
    } finally {
      setLoading(false);
    }
  }

  if (loaded) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3" data-testid="last-order-loaded">
        <div className="flex items-center gap-2">
          <PackageSearch size={14} className="text-emerald-700 shrink-0" />
          <div className="text-xs font-semibold text-emerald-900 flex-1">
            Last order loaded for {loaded.length} contact{loaded.length === 1 ? "" : "s"}
          </div>
          <button type="button" onClick={onClear} className="text-[11px] text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="last-order-clear">
            <XIcon size={12} /> Back to contacts
          </button>
        </div>
        <ul className="mt-2 max-h-28 overflow-y-auto divide-y divide-emerald-100 text-[11px]" data-testid="last-order-list">
          {loaded.map((o) => (
            <li key={o.order_record_id} className="py-1 flex items-center gap-2">
              <span className="flex-1 truncate text-zinc-800">{o.full_name}</span>
              <span className="font-mono text-zinc-600">#{o.order_id}</span>
              <span className={`uppercase tracking-wide text-[9px] px-1.5 py-0.5 rounded-full ${o.status === "completed" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{o.status}</span>
              {o.tracking_number ? <Truck size={11} className="text-blue-600" title={o.tracking_number} /> : null}
              <span className="text-zinc-700 font-medium">{fmt(o.total)}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-zinc-200 bg-zinc-50/70 p-3" data-testid="last-order-loader">
      <div className="flex items-center gap-2">
        <PackageSearch size={14} className="text-violet-600 shrink-0" />
        <div className="text-xs font-semibold text-zinc-800 flex-1">Load each contact's last order</div>
      </div>
      <p className="text-[10px] text-zinc-500 mt-0.5">
        Fills the Order Confirmation / Order Shipped templates with that order's items, totals, addresses and tracking.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {STATUSES.map((s) => (
          <button
            key={s.key || "any"}
            type="button"
            onClick={() => setStatus(s.key)}
            className={`text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors ${status === s.key ? "bg-violet-600 border-violet-600 text-white" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"}`}
            data-testid={`last-order-status-${s.key || "any"}`}
          >
            {s.label}
          </button>
        ))}
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="ml-auto h-8 px-3 rounded-lg text-xs font-semibold bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="last-order-load-btn"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <PackageSearch size={12} />}
          Load last order
        </button>
      </div>
    </div>
  );
}
